<template>
  <nav>
    <a
      v-for="option in props.viewOptions"
      role="button"
      :key="option"
      class="link"
      :class="{ selected: option === currentViewOptionState }"
      :data-cy="`${props.translationNamespace}.${option}`"
      :style="{ width: props.customWidth || 'fit-content' }"
      v-on:click="selectViewOption(option)">
      <span>{{ $t(`${props.translationNamespace}.${option}`) }}</span>
    </a>
  </nav>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export interface Props {
  toggleId: string;
  translationNamespace: string;
  viewOptions: string[];
  defaultViewOption?: string;
  customWidth?: string;
}

const props = defineProps<Props>();

const selectionChanged = defineEmits<{
  (event: 'view-option-updated', option: string): void;
}>();

const route = useRoute();
const router = useRouter();

const currentViewOptionState = ref<string>();

const defaultViewOption = computed(() => props.defaultViewOption || props.viewOptions[0]);

if (route.query[`${props.toggleId}-show`]) {
  currentViewOptionState.value = route.query[`${props.toggleId}-show`] as string;
} else {
  currentViewOptionState.value = defaultViewOption.value;
}

const updateQueryParam = (option: string) => {
  router.replace({ query: { ...route.query, [`${props.toggleId}-show`]: option } });
};

const selectViewOption = (option: string) => {
  currentViewOptionState.value = option;
  selectionChanged('view-option-updated', option);

  if (route.query[`${props.toggleId}-show`] !== currentViewOptionState.value) {
    updateQueryParam(option);
  }
};
</script>

<style lang="scss" scoped>
nav {
  display: flex;
  width: fit-content;
  border-radius: 180px;
  background-color: var(--bg-page-color);
  font-family: inherit;
  text-transform: none;
  font-size: 0.875rem;
  padding: 0;

  a {
    display: flex;
    align-items: center;
    height: 42px;
    border: none;
    padding: 0px 20px;
    justify-content: center;
    border-radius: 180px;
    transition:
      box-shadow 0.1s ease,
      transform 0.1s ease;

    &.selected {
      background-color: var(--primary-bg-color);
      border: 1px solid var(--primary-button-bg-color);
      box-shadow:
        0px 0px 1px var(--shadow-color),
        0px 4px 8px var(--shadow-color);
      transform: scale(1.1);
    }
  }
}
</style>
